import { CHIEF_COMPLAINT } from '../enums';

export const prebillerVerifiedLocationMissing = ({ map, location }) =>
  !(
    (map.isPickupResidence || location.fromSelected.facility) &&
    (map.isDestinationResidence || location.toSelected.facility)
  );

export const patientAddressMissing = ({ patient }) =>
  !(patient.address1 && patient.city && patient.state && patient.zip && patient.zip.trim().length === 5);

export const prebillerVerifiedPayerMissing = ({ infobox, agency }) => {
  if (isBilledToFacilityDataLoaded(infobox, agency)) {
    const billToFacilityCoverage = Object.values(infobox.coverages).find(
      c => c.coordinationTypeId === infobox.tempBillToFacilityCoverage.coordinationTypeId,
    );
    if (!billToFacilityCoverage || billToFacilityCoverage.payer.payerId !== agency.billToFacilityPayerId) {
      return true;
    }

    return !isPayerValid(billToFacilityCoverage);
  }

  return !Object.values(infobox.coverages)
    .map(coverage => coverage.isActive)
    .reduce((acc, value) => acc || value, false);
};

export const payerNotAllowed = ({ infobox }) =>
  Object.values(infobox.coverages).some(
    c => c.payer.typeId === window.ambulnzConfig.payerTypes.EXTERNAL_NON_MATCHED_PAYER,
  );

const isPayerValid = payer => !!(payer.payerName && payer.policyNumber && payer.authorizationNumber);

const isBilledToFacilityDataLoaded = (infobox, agency) =>
  !!(agency.billToFacilityPayerId && infobox.isBilledToFacility && infobox.tempBillToFacilityCoverage);

export const disabledByPcrStatus = ({
  patientDocuments,
  service,
  requests: { isBillerVerified, request },
  user: { isUserAmbAdmin },
}) => {
  const hasPcr = !!patientDocuments.attachments.some(a => a.attachmentType.type === 'PCR');
  const skipPcrCheck = JSON.parse(window.ambulnzConfig.constants.SKIP_PCR_CHECK_FOR_SERVICE_TYPES).includes(
    service.serviceId,
  );
  const pcrValid = skipPcrCheck || hasPcr;
  return !isUserAmbAdmin && request?.isCompleted && isBillerVerified && pcrValid;
};

export const chiefComplaintMissing = ({ requests }) =>
  !requests.chiefComplaintId && !requests.chiefComplaintName && !requests.chiefComplaintType;

export const chiefComplaintNotAllowed = ({ requests }) =>
  requests.chiefComplaintType === CHIEF_COMPLAINT.TYPES.NON_STANDARD;

export const patientInvalid = state =>
  !(state.infobox.patientFirstName && state.infobox.patientLastName && state.infobox.patientBirthday);

export const changesNotSavedYet = state =>
  !!state.formFlow.submitInProgress || !!state.formFlow.autoSaveInProgress || !!state.formFlow.dirty;

export const patientWeightHeightMissing = state =>
  !(!state.service.bariatric || !!(state.infobox.patientWeight && state.infobox.patientHeight));

const notAdmin = ({ user }) => !user.isUserAmbAdmin;

const requiresEaRunBeforePrebillerVerification = state =>
  !state.user.isUserAmbAdmin &&
  !!window.ambulnzConfig.constants.REQUIRE_EA_RUN_BEFORE_MANUAL_PREBILLER_VERIFIED &&
  !state.requests.isBillerVerified;

export const prebillerVerifiedEaRunStatusUnavailable = state =>
  requiresEaRunBeforePrebillerVerification(state) &&
  !!state.requests.request?.id &&
  state.eligibility.runStatus.requestId === state.requests.request.id &&
  state.eligibility.runStatus.status === 'failure';

export const prebillerVerifiedEaRunMissing = state =>
  requiresEaRunBeforePrebillerVerification(state) &&
  (!state.requests.request?.id ||
    state.eligibility.runStatus.requestId !== state.requests.request.id ||
    !['success', 'failure'].includes(state.eligibility.runStatus.status) ||
    (state.eligibility.runStatus.status === 'success' && !state.eligibility.runStatus.hasRun));

export const prebillerVerifiedDisabled = state => {
  if (prebillerVerifiedEaRunMissing(state)) return true;

  if (!notAdmin(state) && changesNotSavedYet(state)) {
    return true;
  }

  if (notAdmin(state)) {
    if (state.requests.isBillerVerified) {
      return true;
    }
    return (
      prebillerVerifiedLocationMissing(state) ||
      patientWeightHeightMissing(state) ||
      patientAddressMissing(state) ||
      prebillerVerifiedPayerMissing(state) ||
      payerNotAllowed(state) ||
      disabledByPcrStatus(state) ||
      chiefComplaintMissing(state) ||
      chiefComplaintNotAllowed(state) ||
      patientInvalid(state) ||
      changesNotSavedYet(state)
    );
  }

  return false;
};
